import { withoutTrailingSlash } from 'ufo';
import groq from 'groq';
import type { LOCALE_CODE } from '@pkg/config/languages/localeCodes';
import { SINGLETON_DOCUMENT } from '@pkg/config/sanity/singletons';
import { singletonDocIdByName } from '@pkg/sanity-utils/singletonDocId';
import { pathPartsToSlugParts, urlToPathParts } from '@pkg/sanity-utils/urlToSlugParts';
import {
  businessHomepageTranslationFields,
  businessPageFields,
  type BusinessPageContent,
} from '../apis/sanity/queries/pageById';
import { businessPageBySlug } from '../apis/sanity/queries/pageBySlug';

export type { BusinessPageContent };

export type BusinessSanityPageResult = {
  page: BusinessPageContent | null;
};

/**
 * Business page resolution:
 * - locale home → business homepage singleton
 * - other paths → slug page with business/common audience (shared nesting rules)
 */
export function useSanityPageQuery<
  Type extends BusinessSanityPageResult = BusinessSanityPageResult,
>() {
  const route = useRoute();
  const { locale } = useI18n();
  const localePath = useLocalePath();
  const unrefLocale = unref(locale) as LOCALE_CODE;

  const homePath = withoutTrailingSlash(localePath('/'));
  const isHome = withoutTrailingSlash(route.path) === homePath;

  if (isHome) {
    const documentId = singletonDocIdByName(
      SINGLETON_DOCUMENT.BUSINESS_HOMEPAGE,
      unrefLocale,
    );

    const query = groq`{
      "page": *[_id == $id][0]{
        ${businessPageFields},
        ${businessHomepageTranslationFields}
      }
    }`;

    return useFetch<Type>('/api/sanity-fetch', {
      method: 'POST',
      body: {
        query,
        params: {
          id: documentId,
          locale: unrefLocale,
        },
      },
      key: `business-home-${documentId}`,
    });
  }

  const pathParts = urlToPathParts(route.path);
  const slugParts = pathPartsToSlugParts(pathParts);

  if (!pathParts.length || !slugParts.slug || slugParts.slug === '/') {
    return useAsyncData<Type>(
      `business-page-${unrefLocale}-empty`,
      () => Promise.resolve({ page: null } as Type),
    );
  }

  return useFetch<Type>('/api/sanity-fetch', {
    method: 'POST',
    body: {
      query: businessPageBySlug(slugParts),
      params: {
        locale: unrefLocale,
        ...slugParts,
      },
    },
    key: `business-page-${unrefLocale}-${route.path}`,
  });
}
